import { UntypedFormGroup } from '@angular/forms';
import moment, { isMoment, Moment } from 'moment';
import { Observable, of } from 'rxjs';
import { Column } from '@miq/catalog';
import { formatDate, isOpportunityTabActive, isTenantEntityActive } from '../../../../../../lib/shared/utils/functions';
import { ConversionType, EfficiencyTransport } from '../../../../../../lib/models/efficiencies.models';
import { DataObject } from '../../../../../../lib/models/data-object.models';
import { entityParamsOverlay, FieldsLeaseSpace, FieldsTenantSpace } from '../details-overlay/details-overlay.constants';
import { EntitySPSpaces, OPTIONS, SPRequest } from '../details-overlay/details-overlay.model';
import { SuiteType } from '../item.model';
import { DetailsMenu, TermDuration } from './details-modal.model';
import { DetailsModalComponent } from './details-modal.component';
import { ConfirmData } from '../../../../dialogs/confirm-dialog-sp/confirm-dialog-sp.const';

export const checkInstance = (instance: DetailsModalComponent): boolean => {
  const isComponentInstance = instance instanceof DetailsModalComponent;
  return isComponentInstance;
};

function onSpaceReplacement(conversionType: ConversionType) {
  let preReplacementAction$: Observable<EfficiencyTransport | string> = of('');
  if (conversionType === ConversionType.TenancyToNrs) {
    preReplacementAction$ = this.detailsOverlayService.convertTenancyToNRS(this.space.floorNumber, this.space);
  } else if (conversionType === ConversionType.NrsToAvailability) {
    preReplacementAction$ = this.detailsOverlayService.convertNRStoAvailability(
      this.space.floorNumber,
      this.containerRecordFloors,
      this.space
    );
  }
  preReplacementAction$.subscribe(preparedEfficiencyTransport => {
    const simpleEfficiencyTransport = {
      space: this.space,
      floor: this.space.floorNumber,
      conversionType,
      containerRecordFloors: this.containerRecordFloors,
    };
    const efficiencyTransport: EfficiencyTransport =
      preparedEfficiencyTransport !== ''
        ? (preparedEfficiencyTransport as EfficiencyTransport)
        : simpleEfficiencyTransport;
    this.stackingPlanService.replaceSpace(efficiencyTransport);
    this.dialogRef.close();
  });
}

function linkOpportunity() {
  const record = {};
  record['entity'] = this.space.lease.id;
  record['company_contact_role_addresses'] = this.space.tenantInfo.map(item => {
    return { company_id: item.companyId };
  });
  const leaseId = this.space.leaseId;
  this.spLinkOpportunityService.triggerOpportunity(leaseId, record);
}

function disableLinkOpportunity() {
  const leaseId = this.space.leaseId;
  const isConfidential = this.space.isUserLessThanRecordConf || this.space?.lease?.access_control?.private_data_f;
  return !leaseId || isConfidential;
}

function getTenantMenu(): DetailsMenu[] {
  if (!this.stackingPlanService.isTrustedUser()) {
    return [
      {
        action: () => {
          this.editMode = true;
          setTimeout(() => this.detailsFormGroup?.updateValueAndValidity?.({ onlySelf: false, emitEvent: true }));
        },
        text: 'overlayDetails.editFields',
      },
    ];
  }

  return [
    {
      action: () => {
        this.editMode = true;
        setTimeout(() => this.detailsFormGroup?.updateValueAndValidity?.({ onlySelf: false, emitEvent: true }));
      },
      text: 'overlayDetails.editFields',
    },
    {
      action: onSpaceReplacement.bind(this, ConversionType.TenancyToTenancy),
      text: isTenantEntityActive() ? 'overlayDetails.replaceTenancyLease' : 'overlayDetails.replaceLease',
      // ponytail: hidden until the Lease "Expire and Create New Lease" workflow is fixed/replaced (MIQ Stacking Plan bad-data story)
      hidden: true,
    },
    {
      action: onSpaceReplacement.bind(this, ConversionType.TenancyToNrs),
      text: 'overlayDetails.makeANonRentableSpace',
      // ponytail: hidden until the Lease "Make it a Non-Rentable Space" workflow is fixed/replaced (MIQ Stacking Plan bad-data story)
      hidden: true,
    },
    {
      action: onSpaceReplacement.bind(this, ConversionType.TenancyToAvailability),
      text: 'overlayDetails.makeItAvailable',
      // ponytail: hidden until the Lease "Make it Available" workflow is fixed/replaced (MIQ Stacking Plan bad-data story)
      hidden: true,
    },
    {
      action: linkOpportunity.bind(this),
      text: 'overlayDetails.linkCreateOpportunity',
      hidden: !isOpportunityTabActive(),
      disabled: disableLinkOpportunity.bind(this)(),
    },
  ];
}

function verifyEntity() {
  if (!this.stackingPlanService.isTrustedUser()) {
    return;
  }
  this.space.verified_ts = new Date();
  this.stackingPlanService.sendVerifiedEntity(this.space.suiteType, this.space.id).subscribe(() => {
    const message = `${this.translationService.translate('recordsSearch.verifiedSuccessfully')} ${
      this.data.suiteType
    } ${this.translationService.translate('recordsSearch.record')}`;
    const closeMessage = this.translationService.translate('recordsSearch.close');
    this.notificationService.showSnackBack(message, closeMessage);
  });
}

function getVerifiedTextAvailability() {
  return this.space.verified_ts
    ? `${this.translationService.translate('recordsSearch.verified')} ${formatDate(this.space.verified_ts)}`
    : 'recordsSearch.verify';
}

function getAvailabilityMenu(): DetailsMenu[] {
  return [
    {
      action: () => {
        this.editMode = true;
        setTimeout(() => this.detailsFormGroup?.updateValueAndValidity?.({ onlySelf: false, emitEvent: true }));
      },
      text: 'overlayDetails.editFields',
    },
    this.stackingPlanService.isTrustedUser()
      ? {
          action: onSpaceReplacement.bind(this, ConversionType.AvailabilityToTenancy),
          text: isTenantEntityActive()
            ? 'overlayDetails.addTenancyLease'
            : 'overlayDetails.wizardTitleCloseAndCreateLease',
          // ponytail: hidden until the Availability "Close and Create Lease" workflow is fixed/replaced (MIQ Stacking Plan bad-data story)
          hidden: true,
        }
      : null,
    {
      action: verifyEntity.bind(this),
      class: 'verify-btn',
      text: getVerifiedTextAvailability.bind(this)(),
      disabled: !this.stackingPlanService.isTrustedUser(),
    },
  ].filter(Boolean);
}

export function getMenu(): () => DetailsMenu[] {
  if (!checkInstance(this) || !this.space) {
    return () => [];
  }

  switch (this.space.suiteType) {
    case SuiteType.Tenant:
      return getTenantMenu.bind(this)();
    case SuiteType.Availability:
      return getAvailabilityMenu.bind(this)();
    case SuiteType.Lease:
      return getTenantMenu.bind(this)();
    default:
      return () => [];
  }
}

export const getColumnLabel = (columns: Column[], columnId: number, defaultLabel?: string): string => {
  return columns.find(({ columnId: colId }) => colId === columnId)?.header.trim() || defaultLabel;
};

export const yearsAndMonthsTerm = (startDate: string, endDate: string): TermDuration => {
  const now = moment();
  const start = moment(startDate);
  const end = moment(endDate);
  const currentRange = now.add('1', 'day').diff(start);
  const termRange = end.add('1', 'day').diff(start);
  const termPercentage = (currentRange / termRange) * 100;
  const termDuration = moment.duration(termRange);
  return { years: termDuration.years(), months: termDuration.months(), termPercentage };
};

export function generateSpRequest(floor: DataObject): {
  requestData: SPRequest;
  requestCall$: Observable<DataObject>[];
} {
  // * Side effect of dynamic form on subset without dataField value...
  // Get Raw Value is needed to get data of disabled formcontrols as well because
  // lease type will be disabled when it doesn't meet criteria.
  const spFormValue = _getFormData(this.detailsFormGroup);

  const { floorId, floorNumber } = checkIfFloorNumberChange(
    this.containerRecordFloors,
    floor.floor_id,
    floor.floor_number,
    spFormValue.floorNumber
  );

  const requestParams = entityParamsOverlay(
    this.space,
    spFormValue.formGroupSpace?.spaceSizeUOM,
    floorNumber,
    this.stackingPlanService.propertyId,
    getPropertyUsage.call(this).id || 0,
    floorId
  );

  const spRequestObj = Object.entries(spFormValue).reduce((acc, [key, value]) => {
    const isValueObj = value && typeof value === 'object' && !isMoment(value);
    const subValues = isValueObj ? Object.entries(value) : null;
    const field = isValueObj ? subValues[0][0] : key;
    const fieldValue = isValueObj ? subValues[0][1] : value;
    const unit = isValueObj ? subValues[1][0] : null;
    const unitValue = isValueObj ? subValues[1][1] : null;
    const entity = getRequestEntityType(field, this.space.suiteType);

    return {
      ...acc,
      [entity]: {
        ...requestParams[entity],
        ...acc[entity],
        [field]: formatData(fieldValue),
        ...(unit ? { [unit]: unitValue } : {}),
      },
    };
  }, {}) as SPRequest;

  const requestCall$ = Object.entries(spRequestObj).reduce((acc, [key, value]) => {
    const { entity, edpMethod, errorMessage, reqClass, entityKey } = OPTIONS[key];
    const { payloads } =
      key === 'leaseSpace'
        ? new reqClass(value, edpMethod, this.containerRecordFloors)
        : new reqClass(value, edpMethod);

    return [
      ...acc,
      this.actionsToolbarService
        .updateOverlapFormData(payloads, entity, this.space[entityKey])
        .pipe(this.actionsToolbarService.errorHandling(errorMessage)),
    ];
  }, []);

  return { requestData: spRequestObj, requestCall$ };
}

/**
 * Get form data without undefined values & notes if control not dirty so it doesn't get sent to the backend.
 * @param form - FormGroup
 * @returns Record<string, any>
 */
const _getFormData = (form: UntypedFormGroup): Record<string, any> => {
  const rawData = form.getRawValue();
  // TODO: check why this "undefined" is being added to the object and remove it
  delete rawData.undefined;
  return rawData;
};

export const checkIfFloorNumberChange = (
  floors: DataObject[],
  floorId: number,
  currentFloorNumber: string,
  newFloorNumber: string
): { floorId: number; floorNumber: string } => {
  if (!newFloorNumber || currentFloorNumber === newFloorNumber) {
    return { floorId, floorNumber: currentFloorNumber };
  }

  const foundFloor = floors.find(
    floor => floor.floor_number === newFloorNumber || floor.floor_id === Number(newFloorNumber)
  );
  const { floor_id, floor_number } = foundFloor || {};

  return { floorId: floor_id, floorNumber: floor_number };
};

export const getLeaseType = (field: string): EntitySPSpaces =>
  FieldsLeaseSpace.includes(field) ? EntitySPSpaces.LeaseSpace : EntitySPSpaces.Lease;

export const geTenantType = (field: string): EntitySPSpaces =>
  FieldsTenantSpace.includes(field) ? EntitySPSpaces.TenantSpace : EntitySPSpaces.Tenant;

export const getRequestEntityType = (field: string, suiteType: string): EntitySPSpaces => {
  if (suiteType === SuiteType.Availability) {
    return AVAILABILITY_FIELDS.includes(field) ? EntitySPSpaces.AvailabilitySpace : EntitySPSpaces.Availability;
  }

  return isTenantEntityActive() ? geTenantType(field) : getLeaseType(field);
};

function getPropertyUsage(): DataObject {
  if (this.space.suiteType === SuiteType.Availability) {
    return this.stackingPlanService.getPropertyUsage(this.space.refPropertyUsageTypeDesc);
  }
  const entity = !isTenantEntityActive() && this.space.suiteType === SuiteType.Tenant ? 'lease' : 'tenant';
  return this.stackingPlanService.getPropertyUsage(this.space[entity].ref_property_usage_type_desc);
}

export const formatData = (value: any): string | number | Moment => {
  if (!value || isMoment(value) || typeof value === 'boolean') {
    return value;
  }
  const _value = value.replace(/,/g, '');
  return Number.isNaN(Number(_value)) ? value : Number(_value);
};

export const isStackingPlanEntitySpaceActive = (space, suiteType: string): boolean => {
  const entityMapper = {
    lease: 'tenant',
    availability: 'availability',
    tenant: 'tenant',
  };
  return (
    space[entityMapper[suiteType]] != null &&
    !space[entityMapper[suiteType]].miq_record_hide_f &&
    space[entityMapper[suiteType]].active_f
  );
};

export function isDateSameOrAfter(start: Moment, end: Moment): boolean {
  if (!start || !end) {
    return true;
  }
  return moment.utc(end).isSameOrAfter(moment.utc(start));
}

export const isDuplicateSuiteSelected = (floor: DataObject, suiteNumber: string, suiteType: string, suiteId?: number) =>
  (floor.spaces || []).some(
    space =>
      space.suite_number === suiteNumber &&
      isStackingPlanEntitySpaceActive(space, suiteType) &&
      space.suite_id !== suiteId
  );

export const AVAILABILITY_FIELDS = ['suiteNumber', 'floorNumber'];

export const NON_TRUSTED_USER_DATA: ConfirmData = {
  message: 'nonTrustedUser.message',
  doNotRemindMeText: 'nonTrustedUser.doNotRemindMeText',
  rememberOnlyIfTrue: true,
  confirmText: 'nonTrustedUser.confirmText',
  cancelText: 'nonTrustedUser.cancelText',
  rememberMyChoiceDialogId: 'nonTrustedUser',
  showCloseButton: false,
};

export const duplicateSuite = (suiteType): ConfirmData => {
  return {
    message: 'overlayDetails.duplicateSuiteDialogConfirmation.' + suiteType,
    confirmText: 'commonLabels.confirm',
    cancelText: 'commonLabels.cancel',
  };
};
